import axios from 'axios';
import { globalURLS } from '../../Global/urls.js';

export type AdsAudience = 'student' | 'doctor' | null;

export interface AdsCapLimits {
    max?: number | null;
    max_user?: number | null;
}

export interface AdsConfigLimits {
    creatorsOnly: boolean;
    specialties: string[];
    degrees: string[];
    testUsersOnly: boolean;
    audience: AdsAudience;
    usertypes: string[];
    hideAicpFaculty: boolean;
    cta_clicks: AdsCapLimits | null;
    views: AdsCapLimits | null;
}

export interface AdsConfig {
    prim?: number | string;
    ad_id: string | null;
    allowall: boolean;
    cohort?: {
        allow?: string[];
        allowall?: boolean;
        deny?: string[];
    } | null;
    cta_clicks?: AdsCapLimits | null;
    views?: AdsCapLimits | null;
    isactive?: number;
    createdon?: string;
    modifiedon?: string;
    limits: AdsConfigLimits;
    hasConfig: boolean;
}

export interface ActiveAdForConfig {
    ad_id: string;
    displayName?: string | null;
    banner?: string | null;
    ads_gen_id: string;
    ads_gen_name: string;
    section: string;
    type?: string;
    startdate?: string | null;
    enddate?: string | null;
    priority?: number;
    interval?: number;
    config: AdsConfig;
}

export interface AdsConfigOptions {
    audiences: { value: string; label: string }[];
    knownTags: string[];
}

export interface DegreeOption {
    degreeid: string;
    name: string;
}

export interface UsertypeOption {
    usertypeid: string;
    name: string;
}

const post = async <T>(endpoint: string, body: Record<string, unknown> = {}): Promise<T> => {
    const response = await axios.post(
        `${globalURLS.baseURL}${endpoint}`,
        body,
        globalURLS.config,
    );
    if (!response.data?.success) {
        throw new Error(response.data?.message || 'Request failed');
    }
    return response.data.data as T;
};

export const getActiveAdsForConfig = (params: {
    section?: string;
    name?: string;
    ad_id?: string;
} = {}) =>
    post<ActiveAdForConfig[]>('ADgetActiveAdsForConfig', {
        ...(params.section ? { section: params.section } : {}),
        ...(params.name?.trim() ? { name: params.name.trim() } : {}),
        ...(params.ad_id ? { ad_id: params.ad_id } : {}),
    });

export const getAdsConfigOptions = () =>
    post<AdsConfigOptions>('ADgetAdsConfigOptions', {});

export const getAdsConfig = (ad_id: string) =>
    post<AdsConfig>('ADgetAdsConfig', { ad_id });

export const upsertAdsConfig = (payload: {
    ad_id: string;
    limits: AdsConfigLimits;
}) => post<AdsConfig>('ADupsertAdsConfig', payload);

export const deleteAdsConfig = (ad_id: string) =>
    post<{ ad_id: string; deleted: boolean }>('ADdeleteAdsConfig', { ad_id });

export const getDegrees = async (): Promise<DegreeOption[]> => {
    const response = await axios.post(
        `${globalURLS.baseURL}getDegree`,
        {},
        globalURLS.config,
    );
    if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load degrees');
    }
    const raw = response.data.data;
    if (!Array.isArray(raw)) return [];
    return raw
        .map((row: Record<string, unknown>) => {
            const degreeid = String(row.degreeid ?? row.id ?? '');
            const name = String(row.name ?? '');
            return degreeid ? { degreeid, name } : null;
        })
        .filter(Boolean) as DegreeOption[];
};

export const getUsertypes = async (): Promise<UsertypeOption[]> => {
    const response = await axios.post(
        `${globalURLS.baseURL}getUsertypes`,
        {},
        globalURLS.config,
    );
    if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load usertypes');
    }
    const raw = response.data.data;
    if (!Array.isArray(raw)) return [];
    return raw
        .map((row: Record<string, unknown>) => {
            const usertypeid = String(row.usertypeid ?? row.id ?? '');
            const name = String(row.name ?? '');
            return usertypeid ? { usertypeid, name } : null;
        })
        .filter(Boolean) as UsertypeOption[];
};

export const emptyLimits = (): AdsConfigLimits => ({
    creatorsOnly: false,
    specialties: [],
    degrees: [],
    testUsersOnly: false,
    audience: null,
    usertypes: [],
    hideAicpFaculty: false,
    cta_clicks: null,
    views: null,
});

const formatCap = (cap?: AdsCapLimits | null, label?: string) => {
    if (!cap) return null;
    const bits: string[] = [];
    if (cap.max != null && cap.max !== ('' as unknown)) bits.push(`max ${cap.max}`);
    if (cap.max_user != null && cap.max_user !== ('' as unknown)) {
        bits.push(`max/user ${cap.max_user}`);
    }
    if (!bits.length) return null;
    return `${label}: ${bits.join(', ')}`;
};

export const summarizeLimits = (
    limits?: AdsConfigLimits | null,
    allowall?: boolean,
    config?: Pick<AdsConfig, 'cta_clicks' | 'views'> | null,
) => {
    const parts: string[] = [];
    if (limits && !allowall) {
        if (limits.creatorsOnly) parts.push('Creators');
        if (limits.testUsersOnly) parts.push('Test users');
        if (limits.audience === 'doctor') parts.push('Doctors');
        if (limits.audience === 'student') parts.push('Students');
        if (limits.usertypes?.length) parts.push(`${limits.usertypes.length} usertype(s)`);
        if (limits.specialties?.length) {
            parts.push(`${limits.specialties.length} specialty(ies)`);
        }
        if (limits.degrees?.length) parts.push(`${limits.degrees.length} degree(s)`);
    }
    if (limits?.hideAicpFaculty) parts.push('Hide AICP Faculty');

    const cta =
        formatCap(limits?.cta_clicks ?? config?.cta_clicks, 'CTA') || null;
    const views =
        formatCap(limits?.views ?? config?.views, 'Views') || null;
    if (cta) parts.push(cta);
    if (views) parts.push(views);

    if (!parts.length) return 'All users';
    return parts.join(' · ');
};
